<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="5" />

    <div class="pp-page">

      <!-- Header -->
      <div class="pp-header">
        <div class="pp-header-icon">
          <span class="material-symbols-outlined">shield</span>
        </div>
        <div>
          <h2 class="pp-titulo">Políticas de Privacidad y Seguridad</h2>
          <p class="pp-subtitulo">
            Última actualización: {{ fechaActualizacion }} &nbsp;·&nbsp;
            Versión 1.0
          </p>
        </div>
      </div>

      <!-- Índice -->
      <div class="pp-indice">
        <p class="pp-indice-titulo">Contenido</p>
        <ol class="pp-indice-lista">
          <li v-for="(seccion, idx) in secciones" :key="idx">
            <a :href="'#seccion-' + idx" class="pp-indice-link">{{ seccion.titulo }}</a>
          </li>
        </ol>
      </div>

      <!-- Secciones -->
      <div
        v-for="(seccion, idx) in secciones"
        :key="idx"
        :id="'seccion-' + idx"
        class="pp-seccion"
      >
        <div class="pp-seccion-header">
          <div class="pp-seccion-numero">{{ String(idx + 1).padStart(2, '0') }}</div>
          <div class="pp-seccion-icon">
            <span class="material-symbols-outlined">{{ seccion.icono }}</span>
          </div>
          <h3 class="pp-seccion-titulo">{{ seccion.titulo }}</h3>
        </div>
        <div class="pp-seccion-body">
          <p
            v-for="(parrafo, pidx) in seccion.parrafos"
            :key="pidx"
            v-html="parrafo"
            class="pp-parrafo"
          ></p>
          <ul v-if="seccion.lista" class="pp-lista">
            <li v-for="(item, lidx) in seccion.lista" :key="lidx">{{ item }}</li>
          </ul>
        </div>
      </div>

      <!-- Footer de política -->
      <div class="pp-footer-nota">
        <span class="material-symbols-outlined" style="font-size:16px;flex-shrink:0">info</span>
        <span>
          Si tienes preguntas sobre estas políticas, contáctanos en
          <a href="mailto:soporte@kiatros.com">soporte@kiatros.com</a>.
        </span>
      </div>

    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from '@/components/Layout.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'PrivacyPolicy',
  components: { Layout, Footer },

  data() {
    return {
      fechaActualizacion: new Date().toLocaleDateString('es-EC', {
        day: '2-digit', month: 'long', year: 'numeric',
      }),
      secciones: [
        {
          titulo: 'Responsable del tratamiento de datos',
          icono:  'business',
          parrafos: [
            'El responsable del tratamiento de los datos personales recopilados a través de este sistema es <strong>[Nombre de la empresa]</strong>, con domicilio en <strong>[Dirección]</strong>, correo electrónico <strong>[correo@empresa.com]</strong>.',
            'El sistema Kiatros es una plataforma de gestión de atenciones médicas, citas y facturación utilizada exclusivamente por profesionales de salud autorizados.',
          ],
        },
        {
          titulo: 'Datos personales que recopilamos',
          icono:  'database',
          parrafos: [
            'Para el funcionamiento del sistema, recopilamos y procesamos los siguientes datos personales de los pacientes y usuarios:',
          ],
          lista: [
            'Datos de identificación: nombres, apellidos, número de cédula, pasaporte o documento equivalente.',
            'Datos de contacto: dirección, teléfono y correo electrónico.',
            'Datos demográficos: fecha de nacimiento, sexo, estado civil.',
            'Datos clínicos: motivo de consulta, diagnóstico, tratamiento y evolución registrados por profesionales de salud.',
            'Datos de facturación: información necesaria para la emisión de comprobantes de pago.',
            'Datos de acceso: usuario, fecha y hora de ingreso al sistema (con fines de auditoría).',
          ],
        },
        {
          titulo: 'Finalidad del tratamiento',
          icono:  'task',
          parrafos: [
            'Los datos personales recopilados se utilizan exclusivamente para las siguientes finalidades:',
          ],
          lista: [
            'Gestión de citas y atenciones médicas.',
            'Elaboración de historia clínica del paciente.',
            'Emisión de facturas y gestión de cobros.',
            'Generación de reportes estadísticos agregados (sin identificación individual).',
            'Cumplimiento de obligaciones legales y regulatorias en materia de salud.',
          ],
        },
        {
          titulo: 'Base legal del tratamiento',
          icono:  'gavel',
          parrafos: [
            'El tratamiento de datos de salud se realiza bajo las siguientes bases legales:',
            '<strong>Consentimiento del titular</strong>: el paciente autoriza el registro de sus datos al momento de la atención.',
            '<strong>Obligación legal</strong>: el sistema cumple con la normativa ecuatoriana vigente en materia de salud, incluyendo la Ley Orgánica de Protección de Datos Personales (LOPDP) y las disposiciones del Ministerio de Salud Pública.',
            '<strong>Interés legítimo</strong>: la gestión clínica y administrativa necesaria para la prestación del servicio de salud.',
          ],
        },
        {
          titulo: 'Confidencialidad y seguridad',
          icono:  'lock',
          parrafos: [
            'Implementamos medidas técnicas y organizativas para proteger los datos personales contra acceso no autorizado, pérdida, alteración o divulgación. Entre ellas:',
          ],
          lista: [
            'Autenticación con usuario y contraseña para acceso al sistema.',
            'Control de perfiles y permisos por módulo (administración, registros médicos, financiero, dashboards).',
            'Registro de auditoría de accesos y modificaciones.',
            'Cierre automático de sesión por inactividad.',
            'Transmisión de datos mediante conexiones seguras (HTTPS).',
            'Acceso restringido a datos clínicos únicamente a personal autorizado.',
          ],
        },
        {
          titulo: 'Conservación de datos',
          icono:  'history',
          parrafos: [
            'Los datos clínicos se conservan conforme a la normativa sanitaria ecuatoriana vigente, que establece un período mínimo de conservación de la historia clínica.',
            'Los datos de facturación se conservan durante el período exigido por la legislación tributaria.',
            'Los registros de auditoría y acceso se conservan por un período de <strong>2 años</strong>.',
          ],
        },
        {
          titulo: 'Derechos del titular',
          icono:  'person_check',
          parrafos: [
            'De conformidad con la Ley Orgánica de Protección de Datos Personales del Ecuador, el titular de los datos tiene derecho a:',
          ],
          lista: [
            'Acceder a sus datos personales almacenados en el sistema.',
            'Solicitar la rectificación de datos inexactos o incompletos.',
            'Solicitar la supresión de sus datos cuando ya no sean necesarios.',
            'Oponerse al tratamiento de sus datos en determinadas circunstancias.',
            'Solicitar la portabilidad de sus datos en un formato legible.',
          ],
        },
        {
          titulo: 'Modificaciones a esta política',
          icono:  'edit_document',
          parrafos: [
            'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Las modificaciones serán publicadas en el sistema con indicación de la fecha de última actualización.',
            'El uso continuado del sistema tras la publicación de cambios implica la aceptación de la política actualizada.',
          ],
        },
      ],
    }
  },

  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },
}
</script>

<style scoped>
.pp-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
}

/* Header */
.pp-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 1.5rem;
}
.pp-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #185FA5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pp-header-icon .material-symbols-outlined {
  font-size: 26px;
  color: #E6F1FB;
}
.pp-titulo {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 2px;
}
.pp-subtitulo {
  font-size: .8rem;
  color: #9aa0ac;
  margin: 0;
}

/* Índice */
.pp-indice {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 2rem;
}
.pp-indice-titulo {
  font-size: .75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #9aa0ac;
  margin: 0 0 8px;
}
.pp-indice-lista {
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pp-indice-link {
  font-size: .875rem;
  color: #185FA5;
  text-decoration: none;
}
.pp-indice-link:hover { text-decoration: underline; }

/* Secciones */
.pp-seccion {
  margin-bottom: 2rem;
  scroll-margin-top: 80px;
}
.pp-seccion-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: .875rem;
  padding-bottom: .5rem;
  border-bottom: 2px solid #e8e8e8;
}
.pp-seccion-numero {
  font-size: .7rem;
  font-weight: 700;
  color: #9aa0ac;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.pp-seccion-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #E6F1FB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pp-seccion-icon .material-symbols-outlined {
  font-size: 15px;
  color: #185FA5;
}
.pp-seccion-titulo {
  font-size: .95rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.pp-seccion-body { padding-left: 0; }
.pp-parrafo {
  font-size: .875rem;
  color: #495057;
  line-height: 1.7;
  text-align: justify;
  margin-bottom: .75rem;
}
.pp-parrafo:last-child { margin-bottom: 0; }

.pp-lista {
  margin: .5rem 0 0 1rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pp-lista li {
  font-size: .875rem;
  color: #495057;
  line-height: 1.6;
}

/* Nota footer */
.pp-footer-nota {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: .8rem;
  color: #6c757d;
  line-height: 1.5;
  margin-top: 1rem;
}
.pp-footer-nota .material-symbols-outlined { color: #185FA5; }
.pp-footer-nota a { color: #185FA5; }

@media (max-width: 576px) {
  .pp-page { padding: 1rem .5rem 2rem; }
  .pp-titulo { font-size: 1.1rem; }
}
</style>